// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline commands)
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Loader, Segment } from "semantic-ui-react";
import PropTypes from "prop-types";
import moment from "moment";
import _ from "lodash";
import { useSelector } from "react-redux";

import AFPlot from "../../../../Lumber/Plotting/AFPlotly";
import {
	NEUTRAL_CORRELATION_COLOR,
	NEGATIVE_CORRELATION_COLOR,
	POSITIVE_CORRELATION_COLOR,
} from "../../../../../utilities/graphics.ts";
import calculateOverlappingCorrelationValue from "../CalculateOverlappingCorrelationValue";

const toRgb = (c) => `rgb(${c.red},${c.green},${c.blue})`;
const DATE_FORMAT = "MM/DD/YYYY h:mma";

const TimecourseCorrelationMatrix = ({
	plotAnalysisData,
	anchorAssessmentData,
	colors,
	selectedQuantifiedRegionTypeId,
}) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);
	const [structuredPlotAnalysisData, setStructuredPlotAnalysisData] = useState(null);
	const [structuredPlotAnalysisDataLoading, setStructuredPlotAnalysisDataLoading] = useState(true);
	const [processedData, setProcessedData] = useState(null);
	const [processedDataLoading, setProcessedDataLoading] = useState(true);
	const [sortedFlightDataLoading, setSortedFlightDataLoading] = useState(true);
	const [sortedFlightData, setSortedFlightData] = useState(true);
	const [flightDateLookup, setflightDateLookup] = useState([]);
	const [analysisNameLookup, setAnalysisNameLookup] = useState([]);

	// 1. When the plot analysis data changes, structure it.
	useEffect(() => {
		if (plotAnalysisData) {
			structurePlotAnalysisData();
		}
	}, [plotAnalysisData, anchorAssessmentData, selectedQuantifiedRegionTypeId]);

	// 2. Once the structured data becomes available, process the data (calculate pearson coefficients).
	useEffect(() => {
		if (structuredPlotAnalysisDataLoading === false) {
			processStructuredData();
		}
	}, [structuredPlotAnalysisData]);

	useEffect(() => {
		if (processedDataLoading === false) {
			sortFlightData();
		}
	}, [processedData]);

	function structurePlotAnalysisData() {
		let structuredDataTemp = {};
		let localAnalysisNameLookup = analysisNameLookup;
		let localFlightDateLookup = flightDateLookup;

		let filteredQuantifiedRegionData = _.cloneDeep(plotAnalysisData);
		filteredQuantifiedRegionData = _.filter(filteredQuantifiedRegionData, (d) => {
			return d.quantifiedRegionTypeId === selectedQuantifiedRegionTypeId;
		});

		let analysisGroupedData = _.groupBy(filteredQuantifiedRegionData, "analysisId");
		for (const [analysisKey, analysisGrouping] of Object.entries(analysisGroupedData)) {
			structuredDataTemp[analysisKey] = {};

			if (!Object.keys(analysisNameLookup).includes(analysisKey)) {
				localAnalysisNameLookup[analysisKey] = _.find(filteredQuantifiedRegionData, {
					analysisId: analysisKey,
				}).analysisName;
				setAnalysisNameLookup(localAnalysisNameLookup);
			}

			let flightGroupedData = _.groupBy(analysisGrouping, "flightId");
			for (const [flightKey, flightIdGrouping] of Object.entries(flightGroupedData)) {
				let currentFlightDate = new Date(
					_.find(filteredQuantifiedRegionData, {
						flightId: flightKey,
					}).flightDate,
				);

				if (!Object.keys(flightDateLookup).includes(flightKey)) {
					localFlightDateLookup[flightKey] = currentFlightDate;
					setflightDateLookup(localFlightDateLookup);
				}

				structuredDataTemp[analysisKey][flightKey] = flightIdGrouping;
			}
		}

		setStructuredPlotAnalysisData(structuredDataTemp);
		setStructuredPlotAnalysisDataLoading(false);
	}

	function processStructuredData() {
		let processedDataTemp = [];

		for (const [analysisId] of Object.entries(structuredPlotAnalysisData)) {
			processedDataTemp[analysisId] = {};

			for (const [flightId] of Object.entries(structuredPlotAnalysisData[analysisId])) {
				let values = [];
				structuredPlotAnalysisData[analysisId][flightId].forEach((obj) => {
					values.push({
						plotRange: obj.plotRange,
						plotColumn: obj.plotColumn,
						value: obj.value,
						plotId: obj.plotId,
						excludeFromAssessment: obj.excludeFromAssessment,
					});
				});

				processedDataTemp[analysisId][flightId] = calculateOverlappingCorrelationValue(values, anchorAssessmentData);
			}
		}

		setProcessedData(processedDataTemp);
		setProcessedDataLoading(false);
	}

	function sortFlightData() {
		let flightArray = Object.keys(flightDateLookup).map((key) => [key, flightDateLookup[key]]);
		let sortedFlightArray = flightArray.sort((a, b) => a[1] - b[1]);
		setSortedFlightData(sortedFlightArray);
		setSortedFlightDataLoading(false);
	}

	const colorscale = [
		[0, toRgb(colors.negativeColor)],
		[0.5, toRgb(colors.neutralColor)],
		[1, toRgb(colors.positiveColor)],
	];

	let chartData = [];
	let numRows = 0;
	let numCols = 0;
	let chartLayout = {
		autosize: true,
		margin: { l: 20, r: 20, t: 20, b: 20 },
		xaxis: { side: "top", tickangle: 45, automargin: true },
		yaxis: { autorange: "reversed", automargin: true, scaleanchor: "x", scaleratio: 1 },
		paper_bgcolor: "transparent",
		plot_bgcolor: "transparent",
	};

	if (!sortedFlightDataLoading && !processedDataLoading) {
		const analysisIds = Object.keys(structuredPlotAnalysisData);
		numRows = analysisIds.length;
		numCols = sortedFlightData.length;
		const yLabels = analysisIds.map((id) => analysisNameLookup[id]);
		const xLabels = sortedFlightData.map(([flightId]) => moment(flightDateLookup[flightId]).format(DATE_FORMAT));
		const z = analysisIds.map((analysisId) =>
			sortedFlightData.map(([flightId]) => {
				const v = processedData[analysisId]?.[flightId];
				return v != null ? v : null;
			}),
		);
		const text = z.map((row) => row.map((v) => (v != null ? v.toFixed(3) : "-")));

		chartData = [
			{
				type: "heatmap",
				z,
				x: xLabels,
				y: yLabels,
				text,
				texttemplate: "%{text}",
				colorscale,
				zmin: -1,
				zmax: 1,
				showscale: false,
				xgap: 1,
				ygap: 1,
				hovertemplate: "%{y} | %{x}: %{text}<extra></extra>",
			},
		];
		chartLayout = {
			...chartLayout,
			xaxis: { ...chartLayout.xaxis, range: [-0.5, numCols - 0.5], constrain: "domain", constraintoward: "left" },
			yaxis: { ...chartLayout.yaxis, range: [numRows - 0.5, -0.5], constrain: "domain", constraintoward: "top" },
		};
	}

	return (
		<Segment basic style={{ overflow: "auto" }}>
			{sortedFlightDataLoading ? (
				<Loader active={true} />
			) : (
				<div
					style={{
						width: "100%",
						aspectRatio: `${numCols} / ${numRows}`,
						minHeight: `${Math.max(200, numRows * 60 + 180)}px`,
					}}
				>
					<AFPlot
						disableHoverStyle
						data={chartData}
						layout={chartLayout}
						config={{
							responsive: true,
							toImageButtonOptions: { filename: `TimecourseCorrelationMatrix_${entityNames?.trialName}` },
						}}
						useResizeHandler
						style={{ width: "100%", height: "100%" }}
					/>
				</div>
			)}
		</Segment>
	);
};

TimecourseCorrelationMatrix.propTypes = {
	plotAnalysisData: PropTypes.array,
	anchorAssessmentData: PropTypes.array,
	colors: PropTypes.object,
	selectedQuantifiedRegionTypeId: PropTypes.string,
};

TimecourseCorrelationMatrix.defaultProps = {
	plotAnalysisData: [],
	anchorAssessmentData: [],
	colors: {
		negativeColor: NEGATIVE_CORRELATION_COLOR,
		neutralColor: NEUTRAL_CORRELATION_COLOR,
		positiveColor: POSITIVE_CORRELATION_COLOR,
	},
};

export default TimecourseCorrelationMatrix;
